/* ============================================================================
   Round simulator.

   A seeded, deterministic market + four agents trading it. It exists so the
   product can be evaluated end to end before any real agent is wired in, and
   so the layout is exercised by data with realistic shape: fat-tailed token
   moves, correlated houses, losing streaks, and drawdowns.

   Nothing here leaks into the panels -- it produces the same CompetitionState
   the live feed produces. Delete this directory and the app still runs.
   ========================================================================== */

import { AGENTS, MARKETS, PRICE_RAIL, RULES, type Market } from '../../config/competition'
import { RUNTIME } from '../../config/runtime'
import {
  clamp,
  gaussian,
  maxDrawdownPct,
  mulberry32,
  sharpe,
  thin,
  weightedPick,
} from '../../core/math'
import type {
  Agent,
  ChainId,
  CompetitionState,
  EquitySample,
  HouseStanding,
  LeaderRow,
  Portfolio,
  Position,
  Quote,
  Thought,
  Trade,
} from '../../core/types'
import { composeReasoning, tokenCost, type ReasonKind } from './reasoning'

/* --- tuning ---------------------------------------------------------------
   One place for every magic number in the simulation. */
const SIM = {
  /** Minutes of 1m closes retained per market (drives 24h stats + sparks). */
  histMinutes: 1440,
  /** Momentum half-life, in minutes. */
  momDecayPerMin: 0.9,
  momShock: 0.00035,
  /** Probability per minute that a memecoin gaps. */
  jumpChancePerMin: 0.0022,
  /** Seconds between equity samples on the live tail. */
  liveSampleSec: 5,
  /** Only narrate non-trade thinking inside this window before "now". */
  narrateWindowMin: 90,
}

interface Persona {
  /** Seconds between trade decisions. */
  cadenceSec: number
  /** Seconds between spoken observations when not trading. */
  narrateSec: number
  openBias: number
  maxPositions: number
  sizePct: [number, number]
  takeProfitPct: number
  stopPct: number
  /** Max hold, minutes. */
  holdMin: [number, number]
  /** Skill: probability of aligning with the true momentum regime. */
  edge: number
  conviction: [number, number]
  chainBias: Record<ChainId, number>
  shortBias: number
}

const PERSONAS: Record<string, Persona> = {
  ember: {
    cadenceSec: 320,
    narrateSec: 70,
    openBias: 0.32,
    maxPositions: 4,
    sizePct: [0.08, 0.15],
    takeProfitPct: 12,
    stopPct: -6,
    holdMin: [10, 45],
    edge: 0.575,
    conviction: [0.55, 0.92],
    chainBias: { solana: 7, base: 1.5, ethereum: 1.5 },
    shortBias: 0.1,
  },
  halcyon: {
    cadenceSec: 400,
    narrateSec: 82,
    openBias: 0.3,
    maxPositions: 4,
    sizePct: [0.1, 0.18],
    takeProfitPct: 10,
    stopPct: -5,
    holdMin: [16, 80],
    edge: 0.568,
    conviction: [0.5, 0.88],
    chainBias: { solana: 1.2, base: 7, ethereum: 2.6 },
    shortBias: 0.18,
  },
  hydra: {
    cadenceSec: 240,
    narrateSec: 52,
    openBias: 0.45,
    maxPositions: 6,
    sizePct: [0.04, 0.09],
    takeProfitPct: 16,
    stopPct: -8,
    holdMin: [4, 20],
    edge: 0.545,
    conviction: [0.35, 0.8],
    chainBias: { solana: 8, base: 1.4, ethereum: 1 },
    shortBias: 0.08,
  },
  midas: {
    cadenceSec: 280,
    narrateSec: 64,
    openBias: 0.38,
    maxPositions: 3,
    sizePct: [0.14, 0.24],
    takeProfitPct: 14,
    stopPct: -7.5,
    holdMin: [12, 55],
    edge: 0.558,
    conviction: [0.45, 0.95],
    chainBias: { solana: 1, base: 7.5, ethereum: 2.2 },
    shortBias: 0.14,
  },
}

/** One-way execution cost in USD: a percentage floor plus price impact that
    scales with how much of the book you take, and then flat gas. At a $500
    book the gas is the part that hurts, which is exactly the trade-off a real
    small account faces. */
function executionCostUsd(notional: number, liquidity: number): number {
  const pct = clamp(RULES.baseFeePct + (notional / Math.max(1, liquidity)) * 40, 0.05, 2.5)
  return (notional * pct) / 100 + RULES.gasPerFillUsd
}

interface SimMarket extends Market {
  px: number
  mom: number
  hist: number[]
}

interface SimAgent {
  def: Agent
  persona: Persona
  cash: number
  realized: number
  wins: number
  losses: number
  tradeCount: number
  positions: Position[]
  nextDecisionAt: number
  nextNarrateAt: number
  peak: number
  maxDD: number
  equityHist: number[]
  /** Hourly closes only, so Sharpe is annualised off a uniform sample. */
  hourly: number[]
}

let seq = 0
const uid = (p: string) => `${p}_${(seq++).toString(36)}${Math.floor(performance.now() % 997).toString(36)}`

function fakeHash(rand: () => number, chain: ChainId): string {
  const abc = chain === 'solana' ? '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz' : '0123456789abcdef'
  const len = chain === 'solana' ? 44 : 64
  let s = chain === 'solana' ? '' : '0x'
  for (let i = 0; i < len; i++) s += abc[Math.floor(rand() * abc.length)]
  return s
}

export class Simulator {
  private rand: () => number
  private markets: SimMarket[]
  private agents: SimAgent[]
  private trades: Trade[] = []
  private thoughts: Thought[] = []
  private equity: EquitySample[] = []
  private prevRanks: Record<string, number> = {}
  private ranks: Record<string, number> = {}
  private startedAt = 0
  private lastSampleAt = 0
  private lastHourAt = 0
  /** Seconds accumulated toward the next 1m close. */
  private barAccumSec = 0
  /** Grows as the buffer thins, so the curve keeps its full span. */
  private sampleEverySec = SIM.liveSampleSec
  /** Symbols whose price comes from the live feed rather than the RNG. */
  private anchored = new Set<string>()
  private tick = 0

  constructor(seed: number = RUNTIME.seed) {
    this.rand = mulberry32(seed)
    this.markets = MARKETS.map((m) => ({ ...m, px: m.seed, mom: 0, hist: [] }))
    this.agents = AGENTS.map((def) => ({
      def,
      persona: PERSONAS[def.id],
      cash: RULES.seedCapital,
      realized: 0,
      wins: 0,
      losses: 0,
      tradeCount: 0,
      positions: [],
      nextDecisionAt: 0,
      nextNarrateAt: 0,
      peak: RULES.seedCapital,
      maxDD: 0,
      equityHist: [],
      hourly: [],
    }))
  }

  /**
   * Open the round at `now`.
   *
   * Nothing is back-dated: no trades, no positions, no equity before this
   * instant. The round genuinely starts empty and fills in as it runs.
   *
   * What *is* warmed up is price history -- `warmupMinutes` of candles per
   * market, scaled so the last one lands exactly on the live price where the
   * feed gave us one. Agents need a tape to reason about from their first
   * decision, and a market that existed before the competition did is simply
   * true.
   */
  open(now: number, anchors: Record<string, number> = {}): CompetitionState {
    this.startedAt = now
    const steps = Math.max(2, RUNTIME.warmupMinutes)

    for (const m of this.markets) {
      const path = this.generatePath(m, steps, 60)
      const anchor = anchors[m.symbol]
      if (Number.isFinite(anchor) && anchor > 0) {
        this.anchored.add(m.symbol)
        const k = anchor / path[path.length - 1]
        for (let i = 0; i < path.length; i++) path[i] *= k
      }
      m.hist = path.slice(-SIM.histMinutes)
      m.px = m.hist[m.hist.length - 1]
    }

    // First move within seconds rather than a full cadence, so activating the
    // session visibly does something.
    for (const a of this.agents) {
      a.nextDecisionAt = now + 3_000 + this.rand() * 12_000
      a.nextNarrateAt = now + 1_500 + this.rand() * 6_000
    }

    this.lastSampleAt = now
    this.lastHourAt = now
    this.sample(now)
    return this.snapshot(now)
  }

  /**
   * Overwrite a market with a real print from the live feed.
   *
   * The first print for a market that history was not anchored to arrives at
   * whatever the real price happens to be, which can be orders of magnitude
   * away from the simulated one. Snapping to it would teleport every open
   * position's P&L, so instead the market is re-denominated: history, entries,
   * fills and quantities all scale by the same factor. Percentage moves, and
   * therefore every P&L on screen, are untouched.
   */
  setMark(symbol: string, price: number) {
    const m = this.markets.find((x) => x.symbol === symbol)
    if (!m || !Number.isFinite(price) || price <= 0) return
    if (this.anchored.has(symbol)) {
      m.px = price
      return
    }
    this.anchored.add(symbol)
    const k = price / m.px
    if (!Number.isFinite(k) || k <= 0) return
    m.px = price
    m.seed *= k
    for (let i = 0; i < m.hist.length; i++) m.hist[i] *= k
    for (const a of this.agents) {
      for (const p of a.positions) {
        if (p.symbol !== symbol) continue
        p.entry *= k
        p.mark *= k
        p.qty /= k
        if (p.stop !== null) p.stop *= k
        if (p.target !== null) p.target *= k
      }
    }
    for (const t of this.trades) {
      if (t.symbol !== symbol) continue
      t.price *= k
      t.qty /= k
    }
  }

  /** Advance wall-clock by `dtMs` and return the new snapshot. */
  step(now: number, dtMs: number): CompetitionState {
    const dtSec = clamp(dtMs / 1000, 0.05, 5)
    this.tick++
    this.advanceMarkets(dtSec)
    this.runAgents(now, true)
    if (now - this.lastSampleAt >= this.sampleEverySec * 1000) {
      this.sample(now)
      this.lastSampleAt = now
    }
    return this.snapshot(now)
  }

  /* --- market process ---------------------------------------------------- */

  /** One step of the price process, shared by the history pass and the tail. */
  private nextPrice(m: SimMarket, px: number, dtSec: number): number {
    const speed = RUNTIME.simSpeed
    const dtMin = dtSec / 60
    const dtYears = dtSec / 31_536_000
    // Regime drift scales with the market's own volatility, so majors trend
    // gently and thin memecoins run.
    m.mom =
      m.mom * Math.pow(SIM.momDecayPerMin, dtMin) +
      gaussian(this.rand) * SIM.momShock * Math.sqrt(dtMin) * speed * (m.vol / 1.6)
    const shock = gaussian(this.rand) * m.vol * Math.sqrt(dtYears) * speed
    let next = px * Math.exp(m.mom * dtMin + shock)
    // Fat tail: thin books gap, majors mostly do not.
    if (this.rand() < SIM.jumpChancePerMin * dtMin * (m.liquidity < 2e7 ? 3 : 0.4)) {
      next *= 1 + gaussian(this.rand) * 0.09
    }
    return Math.max(next, m.seed * 0.02)
  }

  /** The full history path for one market, generated ahead of the agent loop. */
  private generatePath(m: SimMarket, steps: number, stepSec: number): number[] {
    const path: number[] = []
    let px = m.seed
    for (let i = 0; i < steps; i++) {
      px = this.nextPrice(m, px, stepSec)
      path.push(px)
    }
    return path
  }

  private advanceMarkets(dtSec: number) {
    for (const m of this.markets) {
      // Anchored markets are marked by the live feed, not by the RNG.
      if (this.anchored.has(m.symbol)) continue
      m.px = this.nextPrice(m, m.px, dtSec)
    }

    // The 1m close series has to stay 1m whatever the tick rate is, or the
    // live loop would flush a day of history in a few minutes.
    this.barAccumSec += dtSec
    if (this.barAccumSec >= 60) {
      this.barAccumSec = 0
      for (const m of this.markets) {
        m.hist.push(m.px)
        if (m.hist.length > SIM.histMinutes) m.hist.shift()
      }
    }
  }

  private marketOf(symbol: string): SimMarket {
    return this.markets.find((m) => m.symbol === symbol)!
  }

  /** Percent change over the trailing `minutes` of 1m closes. */
  private mom(m: SimMarket, minutes: number): number {
    const h = m.hist
    if (h.length < 2) return 0
    const back = h[Math.max(0, h.length - 1 - minutes)]
    return back > 0 ? (h[h.length - 1] / back - 1) * 100 : 0
  }

  /* --- agents ------------------------------------------------------------ */

  private runAgents(now: number, narrate: boolean) {
    for (const a of this.agents) {
      this.markPositions(a)
      this.manageExits(a, now)
      while (now >= a.nextDecisionAt) {
        a.nextDecisionAt += a.persona.cadenceSec * 1000 * (0.6 + this.rand() * 0.9)
        this.decide(a, now, narrate)
      }
      while (now >= a.nextNarrateAt) {
        a.nextNarrateAt += a.persona.narrateSec * 1000 * (0.55 + this.rand() * 1.1)
        if (narrate) this.narrate(a, now)
      }
    }
  }

  private markPositions(a: SimAgent) {
    for (const p of a.positions) {
      const m = this.marketOf(p.symbol)
      p.mark = m.px
      const dir = p.side === 'long' ? 1 : -1
      p.pnl = (p.mark - p.entry) * p.qty * dir - p.fees
      p.pnlPct = (p.pnl / p.notional) * 100
    }
  }

  private equityOf(a: SimAgent): number {
    let deployed = 0
    for (const p of a.positions) deployed += p.notional + p.pnl
    return a.cash + deployed
  }

  private manageExits(a: SimAgent, now: number) {
    for (const p of [...a.positions]) {
      const ageMin = (now - p.openedAt) / 60_000
      const maxHold = p.side === 'long' ? a.persona.holdMin[1] : a.persona.holdMin[1] * 0.7
      const hitTp = p.pnlPct >= a.persona.takeProfitPct
      const hitSl = p.pnlPct <= a.persona.stopPct
      const stale = ageMin >= maxHold
      if (!hitTp && !hitSl && !stale) continue
      const kind: ReasonKind = p.pnl >= 0 ? 'take-profit' : 'stop-out'
      this.closePosition(a, p, now, kind)
    }
  }

  private decide(a: SimAgent, now: number, narrate: boolean) {
    const equity = this.equityOf(a)
    const canOpen =
      a.positions.length < a.persona.maxPositions &&
      a.cash > equity * 0.15 &&
      a.positions.length < RULES.maxOpenPositions
    if (canOpen && this.rand() < a.persona.openBias) {
      this.openPosition(a, now, equity)
      return
    }
    if (narrate) this.narrate(a, now, canOpen ? 'scan' : 'risk')
  }

  private candidates(a: SimAgent): SimMarket[] {
    const house = a.def.house
    return this.markets.filter((m) => m.house === 'both' || m.house === house)
  }

  private openPosition(a: SimAgent, now: number, equity: number) {
    const held = new Set(a.positions.map((p) => p.symbol))
    const pool = this.candidates(a).filter((m) => !held.has(m.symbol))
    if (!pool.length) return

    // Weight by chain affinity and by how much the tape is already moving.
    const m = weightedPick(this.rand, pool, (mk) => {
      const heat = 1 + Math.min(4, Math.abs(this.mom(mk, 15)) / 4)
      return a.persona.chainBias[mk.chain] * heat
    })

    const regimeUp = m.mom >= 0
    const aligned = this.rand() < a.persona.edge
    const side: 'long' | 'short' = aligned === regimeUp ? 'long' : 'short'
    // An agent that will not short a falling market stands down rather than
    // buying it anyway. Flipping the side here was quietly cancelling out the
    // whole edge: it turned every correct bearish read into a wrong long.
    if (side === 'short' && this.rand() > a.persona.shortBias) return

    const [lo, hi] = a.persona.sizePct
    const pctOfEquity = clamp(lo + this.rand() * (hi - lo), 0.02, RULES.maxPositionPctOfEquity / 100)
    const notional = Math.min(a.cash * 0.9, equity * pctOfEquity)
    if (notional < RULES.minTicketUsd) return

    const conviction = a.persona.conviction[0] + this.rand() * (a.persona.conviction[1] - a.persona.conviction[0])
    const dir = side === 'long' ? 1 : -1
    const pos: Position = {
      id: uid('pos'),
      agentId: a.def.id,
      chain: m.chain,
      symbol: m.symbol,
      side,
      venue: m.venue,
      qty: notional / m.px,
      entry: m.px,
      mark: m.px,
      notional,
      pnl: 0,
      pnlPct: 0,
      openedAt: now,
      conviction,
      fees: executionCostUsd(notional, m.liquidity),
      stop: m.px * (1 + (a.persona.stopPct / 100) * dir),
      target: m.px * (1 + (a.persona.takeProfitPct / 100) * dir),
    }
    pos.pnl = -pos.fees
    a.cash -= notional + pos.fees
    a.positions.push(pos)
    a.tradeCount++

    const trade: Trade = {
      id: uid('trd'),
      agentId: a.def.id,
      ts: now,
      action: side === 'long' ? 'BUY' : 'SELL',
      chain: m.chain,
      symbol: m.symbol,
      side,
      venue: m.venue,
      qty: pos.qty,
      price: m.px,
      notional,
      pnl: null,
      txHash: fakeHash(this.rand, m.chain),
      thoughtId: null,
    }
    this.pushTrade(trade)
    const th = this.think(a, 'open', m, now, { sizeUsd: notional, conviction, side, equity, pnlPct: 0, holdMin: 0 })
    th.tradeId = trade.id
    trade.thoughtId = th.id
  }

  private closePosition(a: SimAgent, p: Position, now: number, kind: ReasonKind) {
    const m = this.marketOf(p.symbol)
    const exitFee = executionCostUsd(p.notional, m.liquidity)
    p.fees += exitFee
    p.pnl -= exitFee
    p.pnlPct = (p.pnl / p.notional) * 100
    a.positions = a.positions.filter((x) => x.id !== p.id)
    a.cash += p.notional + p.pnl
    a.realized += p.pnl
    a.tradeCount++
    if (p.pnl >= 0) a.wins++
    else a.losses++

    const trade: Trade = {
      id: uid('trd'),
      agentId: a.def.id,
      ts: now,
      action: 'CLOSE',
      chain: p.chain,
      symbol: p.symbol,
      side: p.side,
      venue: p.venue,
      qty: p.qty,
      price: m.px,
      notional: p.notional,
      pnl: p.pnl,
      txHash: fakeHash(this.rand, p.chain),
      thoughtId: null,
    }
    this.pushTrade(trade)
    const th = this.think(a, kind, m, now, {
      sizeUsd: p.notional,
      conviction: p.conviction,
      side: p.side,
      equity: this.equityOf(a),
      pnlPct: p.pnlPct,
      holdMin: (now - p.openedAt) / 60_000,
    })
    th.tradeId = trade.id
    trade.thoughtId = th.id
  }

  private narrate(a: SimAgent, now: number, forced?: ReasonKind) {
    const pool = this.candidates(a)
    const m = weightedPick(this.rand, pool, (mk) => 1 + Math.abs(this.mom(mk, 15)))
    const kinds: ReasonKind[] = a.positions.length
      ? ['scan', 'risk', 'reject', 'scan']
      : ['scan', 'reject', 'scan']
    const kind = forced ?? kinds[Math.floor(this.rand() * kinds.length)]
    const equity = this.equityOf(a)
    const worst = [...a.positions].sort((x, y) => x.pnlPct - y.pnlPct)[0]
    this.think(a, kind, m, now, {
      sizeUsd: a.positions.reduce((s, p) => s + p.notional, 0) || equity * 0.1,
      conviction: 0.4 + this.rand() * 0.4,
      side: 'long',
      equity,
      pnlPct: worst?.pnlPct ?? 0,
      holdMin: 0,
    })
  }

  private think(
    a: SimAgent,
    kind: ReasonKind,
    m: SimMarket,
    now: number,
    extra: {
      sizeUsd: number
      conviction: number
      side: 'long' | 'short'
      equity: number
      pnlPct: number
      holdMin: number
    },
  ): Thought {
    const ctx = {
      agent: a.def,
      rand: this.rand,
      symbol: m.symbol,
      chain: m.chain,
      venue: m.venue,
      px: m.px,
      mom5m: this.mom(m, 5),
      mom1h: this.mom(m, 60),
      liquidity: m.liquidity,
      ...extra,
    }
    const out = composeReasoning(kind, ctx)
    const cost = tokenCost(a.def, out, this.rand)
    const thought: Thought = {
      id: uid('tht'),
      agentId: a.def.id,
      ts: 0,
      phase: out.phase,
      decision: out.decision,
      chain: m.chain,
      symbol: m.symbol,
      headline: out.headline,
      body: out.body,
      signals: out.signals,
      conviction: extra.conviction,
      tradeId: null,
      ...cost,
    }
    thought.ts = now
    this.thoughts.push(thought)
    if (this.thoughts.length > RUNTIME.maxThoughts) this.thoughts.shift()
    return thought
  }

  private pushTrade(t: Trade) {
    this.trades.push(t)
    if (this.trades.length > RUNTIME.maxTrades) this.trades.shift()
  }

  /* --- sampling + snapshot ------------------------------------------------ */

  private sample(t: number) {
    const hourly = t - this.lastHourAt >= 3600_000
    if (hourly) this.lastHourAt = t
    const v: Record<string, number> = {}
    for (const a of this.agents) {
      const eq = this.equityOf(a)
      v[a.def.id] = eq
      a.equityHist.push(eq)
      if (a.equityHist.length > RUNTIME.maxEquitySamples) a.equityHist = thin(a.equityHist)
      if (hourly) a.hourly.push(eq)
      if (eq > a.peak) a.peak = eq
      a.maxDD = Math.min(a.maxDD, (eq / a.peak - 1) * 100)
    }
    this.equity.push({ t, v })
    if (this.equity.length > RUNTIME.maxEquitySamples) {
      this.equity = thin(this.equity)
      this.sampleEverySec *= 2
    }
  }

  /** The offline fallback for the rail: same slots the live feed would fill. */
  private quotes(now: number): Quote[] {
    return PRICE_RAIL.map((rail) => {
      const hist = this.marketOf(rail.symbol).hist
      const px = hist[hist.length - 1] ?? 0
      const open = hist[0] ?? px
      let hi = -Infinity
      let lo = Infinity
      for (const h of hist) {
        if (h > hi) hi = h
        if (h < lo) lo = h
      }
      const spark: number[] = []
      const stride = Math.max(1, Math.floor(hist.length / 56))
      for (let i = 0; i < hist.length; i += stride) spark.push(hist[i])
      if (spark[spark.length - 1] !== px) spark.push(px)
      return {
        chain: rail.chain,
        symbol: rail.symbol,
        label: rail.label,
        note: rail.note,
        price: px,
        change24hPct: open > 0 ? (px / open - 1) * 100 : 0,
        high24h: hi,
        low24h: lo,
        spark,
        updatedAt: now,
      }
    })
  }

  private snapshot(now: number): CompetitionState {
    const portfolios: Record<string, Portfolio> = {}
    for (const a of this.agents) {
      const equity = this.equityOf(a)
      const unrealized = a.positions.reduce((s, p) => s + p.pnl, 0)
      const deployed = a.positions.reduce((s, p) => s + p.notional + p.pnl, 0)
      const closed = a.wins + a.losses
      const hist = a.equityHist
      portfolios[a.def.id] = {
        agentId: a.def.id,
        equity,
        cash: a.cash,
        deployed,
        realized: a.realized,
        unrealized,
        startEquity: RULES.seedCapital,
        peakEquity: a.peak,
        roiPct: (equity / RULES.seedCapital - 1) * 100,
        drawdownPct: (equity / a.peak - 1) * 100,
        maxDrawdownPct: Math.min(a.maxDD, maxDrawdownPct(hist)),
        wins: a.wins,
        losses: a.losses,
        tradeCount: a.tradeCount,
        winRatePct: closed ? (a.wins / closed) * 100 : 0,
        sharpe: sharpe(a.hourly, 8760),
        spark: hist.slice(-64),
      }
    }

    const ranked = [...this.agents]
      .map((a) => portfolios[a.def.id])
      .sort((x, y) => y.equity - x.equity)
    const leaderboard: LeaderRow[] = ranked.map((p, i) => ({
      rank: i + 1,
      prevRank: this.prevRanks[p.agentId] ?? i + 1,
      agentId: p.agentId,
      equity: p.equity,
      pnl: p.equity - p.startEquity,
      roiPct: p.roiPct,
      winRatePct: p.winRatePct,
      tradeCount: p.tradeCount,
      maxDrawdownPct: p.maxDrawdownPct,
      sharpe: p.sharpe,
    }))
    // Remember the ranking only when it actually changes, so the movement
    // arrow points at the last real overtake rather than at noise.
    const next: Record<string, number> = {}
    for (const r of leaderboard) next[r.agentId] = r.rank
    const changed = leaderboard.some((r) => this.ranks[r.agentId] !== r.rank)
    if (changed) {
      this.prevRanks = this.ranks
      this.ranks = next
      for (const r of leaderboard) r.prevRank = this.prevRanks[r.agentId] ?? r.rank
    }

    const houses: HouseStanding[] = (['pumpfun', 'fomo'] as const).map((house) => {
      const members = this.agents.filter((a) => a.def.house === house)
      const equity = members.reduce((s, a) => s + portfolios[a.def.id].equity, 0)
      const start = members.length * RULES.seedCapital
      return {
        house,
        equity,
        pnl: equity - start,
        roiPct: (equity / start - 1) * 100,
        tradeCount: members.reduce((s, a) => s + a.tradeCount, 0),
        dominance: 0,
      }
    })
    houses[0].dominance = Math.tanh((houses[0].roiPct - houses[1].roiPct) / 12)
    houses[1].dominance = -houses[0].dominance

    return {
      meta: {
        round: RULES.round,
        startedAt: this.startedAt,
        endsAt: this.startedAt + RULES.durationHours * 3600_000,
        seedCapital: RULES.seedCapital,
        phase: 'running',
        status: 'live',
        source: 'mock',
        prices: 'offline',
        priceSource: '',
        tick: this.tick,
      },
      quotes: this.quotes(now),
      portfolios,
      positions: this.agents.flatMap((a) => a.positions.map((p) => ({ ...p }))),
      trades: this.trades.slice(-RUNTIME.maxTrades),
      thoughts: this.thoughts.slice(-RUNTIME.maxThoughts),
      equity: this.equity,
      leaderboard,
      houses,
      version: 0,
    }
  }
}
